import type { ExamTypeConfig } from '@examflow/core';

/**
 * 配置缓存
 */
const configCache = new Map<string, ExamTypeConfig>();

/**
 * 已注册的配置
 */
const registeredConfigs = new Map<string, ExamTypeConfig>();

/**
 * 注册考试配置
 */
export function registerExamConfig(examId: string, config: ExamTypeConfig): void {
  if (config.id !== examId) {
    throw new Error(`Config ID mismatch: expected ${examId}, got ${config.id}`);
  }
  registeredConfigs.set(examId, config);
  configCache.set(examId, config);
}

/**
 * 同步获取考试配置（如果已注册）
 */
export function getExamConfig(examId: string): ExamTypeConfig | undefined {
  // 先检查缓存
  if (configCache.has(examId)) {
    return configCache.get(examId);
  }

  // 检查已注册的配置
  if (registeredConfigs.has(examId)) {
    const config = registeredConfigs.get(examId)!;
    configCache.set(examId, config);
    return config;
  }

  return undefined;
}

/**
 * 加载考试配置。
 * 全部 Exam 在 `@examflow/exams` 入口同步 `registerExamConfig`（见 `src/index.ts`）；
 * 不再用动态 `import(./exams/${id})`，避免 tsup/esbuild empty-glob 告警与打包路径分裂。
 */
export async function loadExamConfig(examId: string): Promise<ExamTypeConfig> {
  const config = getExamConfig(examId);
  if (config) return config;

  throw new Error(
    `Failed to load exam config for ${examId}: not registered. Import @examflow/exams (main entry) so exam configs register at startup.`
  );
}

/**
 * 清除配置缓存
 */
export function clearConfigCache(): void {
  configCache.clear();
}

/**
 * 获取所有已注册的考试 ID
 */
export function getRegisteredExamIds(): string[] {
  return Array.from(registeredConfigs.keys());
}
